//@ts-nocheck
import { Project } from '@/types';
import { Calendar, Clock, ConstructionIcon, FileText } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';

const fmt = (d?: string) =>
  d
    ? new Date(d).toLocaleDateString(undefined, { dateStyle: 'medium' })
    : 'N/A';

interface StatCardProps {
  label: string;
  value: React.ReactNode;
  sub?: string;
  icon: React.ComponentType<{ className?: string }>;
}

const StatCard = ({ label, value, sub, icon: Icon }: StatCardProps) => (
  <div className="bg-card border border-border/60 rounded-lg p-4 space-y-2">
    <div className="flex items-center justify-between">
      <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
        {label}
      </span>
      <Icon className="h-3.5 w-3.5 text-muted-foreground" />
    </div>
    <p className="text-2xl font-bold text-foreground leading-none">{value}</p>
    {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
  </div>
);

export const ProjectDetailCard = ({ project }: { project: Project }) => {
  const start = new Date(project.startDate);
  const end = new Date(project.endDate);
  const today = new Date();
  const total = end.getTime() - start.getTime();
  const elapsed = today.getTime() - start.getTime();
  const daysLeft = Math.ceil((end.getTime() - today.getTime()) / 86_400_000);
  const progress = Math.min(Math.max((elapsed / total) * 100, 0), 100);
  const isOverdue = daysLeft < 0;
  const isUpcoming = start > today;

  return (
    <div className="px-4 py-5 bg-muted/20 border-t border-border/50 space-y-4">
      {/* ── Stat row ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard
          label="Assets"
          value={project.assets?.length ?? 0}
          sub="linked to project"
          icon={ConstructionIcon}
        />
        <StatCard
          label={isOverdue ? 'Overdue' : isUpcoming ? 'Starts in' : 'Remaining'}
          value={isUpcoming ? `${Math.abs(daysLeft)}d` : Math.abs(daysLeft)}
          sub={
            isOverdue
              ? 'days past deadline'
              : isUpcoming
                ? 'days from now'
                : 'days left'
          }
          icon={Clock}
        />
        <StatCard
          label="Start"
          value={
            <span className="text-base font-semibold">
              {fmt(project.startDate)}
            </span>
          }
          icon={Calendar}
        />
        <StatCard
          label="End"
          value={
            <span className="text-base font-semibold">
              {fmt(project.endDate)}
            </span>
          }
          icon={Calendar}
        />
      </div>

      {/* ── Progress bar ── */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            Timeline Progress
          </span>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-foreground">
              {Math.round(progress)}%
            </span>
            <Badge
              variant="outline"
              className={cn(
                'h-4 px-1.5 text-[10px] font-medium',
                isOverdue
                  ? 'bg-red-500/10 text-red-600 border-red-500/20'
                  : isUpcoming
                    ? 'bg-muted text-muted-foreground border-border'
                    : 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
              )}
            >
              {isOverdue ? 'Overdue' : isUpcoming ? 'Upcoming' : 'Active'}
            </Badge>
          </div>
        </div>
        <Progress value={progress} className="h-1.5" />
      </div>

      {/* ── Description ── */}
      {project.description && (
        <div className="bg-card border border-border/60 rounded-lg px-4 py-3 space-y-1.5">
          <div className="flex items-center gap-1.5">
            <FileText className="h-3 w-3 text-muted-foreground" />
            <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
              Description
            </span>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {project.description}
          </p>
        </div>
      )}
    </div>
  );
};
